import type { DocumentNode } from 'graphql'
import { action, makeObservable, observable, runInAction } from 'mobx'

import {
  IStruxTagActivitySessionsQuery,
  IStruxTagActivitySessionsQueryVariables,
  StruxTagActivitySessionsDocument,
} from '~/client/graph/operations/generated/StruxTagActivitySessions.generated'
import {
  IStruxTagGpsPathQuery,
  IStruxTagGpsPathQueryVariables,
  StruxTagGpsPathDocument,
} from '~/client/graph/operations/generated/StruxTagGpsPath.generated'
import {
  IStruxTagUtilizationQuery,
  IStruxTagUtilizationQueryVariables,
  StruxTagUtilizationDocument,
} from '~/client/graph/operations/generated/StruxTagUtilization.generated'

import type GraphExecutorStore from './GraphExecutor.store'

export type StruxTagHistoryState<T> =
  | { readonly _tag: 'NotAsked' }
  | { readonly _tag: 'Loading' }
  | { readonly _tag: 'Ready'; readonly data: T }
  | { readonly _tag: 'Failed'; readonly error: string }

type QueryStore<TData> = {
  readonly states: Map<string, StruxTagHistoryState<TData>>
  readonly inFlight: Map<string, Promise<void>>
}

const NOT_ASKED = { _tag: 'NotAsked' } as const

const errorMessage = (cause: unknown): string =>
  cause instanceof Error ? cause.message : 'StruxTag history request failed'

export default class StruxTagHistoryStore {
  private readonly sessions: QueryStore<IStruxTagActivitySessionsQuery> = {
    states: observable.map(),
    inFlight: new Map(),
  }
  private readonly utilization: QueryStore<IStruxTagUtilizationQuery> = {
    states: observable.map(),
    inFlight: new Map(),
  }
  private readonly gpsPaths: QueryStore<IStruxTagGpsPathQuery> = {
    states: observable.map(),
    inFlight: new Map(),
  }
  private generation = 0

  public constructor(
    private readonly graph: Pick<GraphExecutorStore, 'query'>,
  ) {
    makeObservable(this)
  }

  public sessionsFor(
    variables: IStruxTagActivitySessionsQueryVariables,
  ): StruxTagHistoryState<IStruxTagActivitySessionsQuery> {
    return this.sessions.states.get(this.sessionsKey(variables)) ?? NOT_ASKED
  }

  public utilizationFor(
    variables: IStruxTagUtilizationQueryVariables,
  ): StruxTagHistoryState<IStruxTagUtilizationQuery> {
    return (
      this.utilization.states.get(this.utilizationKey(variables)) ?? NOT_ASKED
    )
  }

  public gpsPathFor(
    variables: IStruxTagGpsPathQueryVariables,
  ): StruxTagHistoryState<IStruxTagGpsPathQuery> {
    return this.gpsPaths.states.get(this.gpsPathKey(variables)) ?? NOT_ASKED
  }

  public ensureSessions(
    variables: IStruxTagActivitySessionsQueryVariables,
  ): Promise<void> {
    return this.ensure(
      this.sessions,
      this.sessionsKey(variables),
      StruxTagActivitySessionsDocument,
      variables,
    )
  }

  public ensureUtilization(
    variables: IStruxTagUtilizationQueryVariables,
  ): Promise<void> {
    return this.ensure(
      this.utilization,
      this.utilizationKey(variables),
      StruxTagUtilizationDocument,
      variables,
    )
  }

  public ensureGpsPath(
    variables: IStruxTagGpsPathQueryVariables,
  ): Promise<void> {
    return this.ensure(
      this.gpsPaths,
      this.gpsPathKey(variables),
      StruxTagGpsPathDocument,
      variables,
    )
  }

  @action.bound public clear(): void {
    this.generation += 1
    this.sessions.states.clear()
    this.sessions.inFlight.clear()
    this.utilization.states.clear()
    this.utilization.inFlight.clear()
    this.gpsPaths.states.clear()
    this.gpsPaths.inFlight.clear()
  }

  private ensure<TData>(
    query: QueryStore<TData>,
    key: string,
    document: DocumentNode,
    variables: object,
  ): Promise<void> {
    const active = query.inFlight.get(key)
    if (active) return active
    if (query.states.get(key)?._tag === 'Ready') return Promise.resolve()

    const generation = this.generation
    runInAction(() => {
      query.states.set(key, { _tag: 'Loading' })
    })

    const request = this.graph
      .query(document, variables)
      .then(result => {
        if (this.generation !== generation) return
        runInAction(() => {
          if (result.error) {
            query.states.set(key, {
              _tag: 'Failed',
              error: errorMessage(result.error),
            })
            return
          }
          if (!result.data) {
            query.states.set(key, {
              _tag: 'Failed',
              error: 'StruxTag history response missing data',
            })
            return
          }
          query.states.set(key, {
            _tag: 'Ready',
            data: result.data as TData,
          })
        })
      })
      .catch(cause => {
        if (this.generation !== generation) return
        runInAction(() => {
          query.states.set(key, {
            _tag: 'Failed',
            error: errorMessage(cause),
          })
        })
      })
      .finally(() => {
        if (query.inFlight.get(key) === request) {
          query.inFlight.delete(key)
        }
      })

    query.inFlight.set(key, request)
    return request
  }

  private sessionsKey(
    variables: IStruxTagActivitySessionsQueryVariables,
  ): string {
    return JSON.stringify([
      variables.projectId,
      variables.infuseId,
      variables.from ?? null,
      variables.to ?? null,
    ])
  }

  private utilizationKey(
    variables: IStruxTagUtilizationQueryVariables,
  ): string {
    return JSON.stringify([
      variables.projectId,
      variables.infuseId,
      variables.windowDays ?? null,
    ])
  }

  private gpsPathKey(variables: IStruxTagGpsPathQueryVariables): string {
    return JSON.stringify([
      variables.projectId,
      variables.infuseId,
      variables.from,
      variables.to,
      variables.maxPoints ?? null,
    ])
  }
}
